import { httpsCallable } from 'firebase/functions';

import { getFirebaseFunctions } from '@/lib/firebase/functions';
import { resolveBrowserQueryCache, type BrowserQueryCache } from '@/lib/query-cache/query-cache';

type ResolveStorageMediaUrlResponse = {
  url: string;
};

type ResolveStorageMediaUrlRequest = {
  url: string;
};

const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg', 'avif']);
const firebaseStorageDownloadUrlPattern = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/(.+)$/i;
const gsUrlPattern = /^gs:\/\/([^/]+)\/(.+)$/i;
const googleStorageUrlPattern = /^https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/i;

export type StorageMediaLocation = {
  bucket: string;
  path: string;
};

function decodeStorageObjectPath(encodedPath: string): string {
  try {
    return decodeURIComponent(encodedPath.replace(/\+/g, '%20'));
  } catch {
    return encodedPath;
  }
}

export function extractStorageMediaLocation(url: string): StorageMediaLocation | null {
  const firebaseMatch = url.match(firebaseStorageDownloadUrlPattern);

  if (firebaseMatch) {
    return {
      bucket: firebaseMatch[1]!,
      path: decodeStorageObjectPath(firebaseMatch[2]!.split('?')[0] ?? ''),
    };
  }

  const gsMatch = url.match(gsUrlPattern);

  if (gsMatch) {
    return {
      bucket: gsMatch[1]!,
      path: decodeStorageObjectPath(gsMatch[2]!),
    };
  }

  const googleStorageMatch = url.match(googleStorageUrlPattern);

  if (googleStorageMatch) {
    return {
      bucket: googleStorageMatch[1]!,
      path: decodeStorageObjectPath(googleStorageMatch[2]!),
    };
  }

  return null;
}

function getPathExtension(path: string): string | null {
  const fileName = path.split('/').filter(Boolean).pop() ?? '';
  const match = fileName.match(/\.([a-z0-9]+)$/i);

  return match?.[1]?.toLowerCase() ?? null;
}

function getUrlPath(url: string): string | null {
  const location = extractStorageMediaLocation(url);

  if (location) {
    return location.path;
  }

  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

export function isStorageMediaUrl(url: string): boolean {
  return extractStorageMediaLocation(url) !== null;
}

export function getMediaKind(url: string): 'pdf' | 'image' | 'other' {
  const path = getUrlPath(url);

  if (!path) {
    return 'other';
  }

  const extension = getPathExtension(path);

  if (extension === 'pdf') {
    return 'pdf';
  }

  if (extension && imageExtensions.has(extension)) {
    return 'image';
  }

  return 'other';
}

export function isPdfMediaUrl(url: string): boolean {
  return getMediaKind(url) === 'pdf';
}

export function isImageMediaUrl(url: string): boolean {
  return getMediaKind(url) === 'image';
}

export async function resolveStorageMediaUrl(
  url: string,
  options?: { cache?: BrowserQueryCache },
): Promise<string> {
  if (!isStorageMediaUrl(url)) {
    return url;
  }

  const cache = resolveBrowserQueryCache(options?.cache);
  const callable = httpsCallable<ResolveStorageMediaUrlRequest, ResolveStorageMediaUrlResponse>(
    getFirebaseFunctions(),
    'resolveStorageMediaUrl',
  );

  const result = await cache.read(
    ['storage-media-url', url],
    async () => {
      const response = await callable({ url });
      return response.data.url;
    },
    {
      staleTimeMs: 50 * 60 * 1000,
      cacheTimeMs: 60 * 60 * 1000,
      tags: ['storage-media'],
    },
  );

  return result.value;
}
